// Upload Service v2 platform adapter for the web app (see core.js for the
// contract). Parts are Blob slices sent with XMLHttpRequest, which reports
// upload progress and can be aborted.

// A part that sends nothing (and gets no response) for this long is retried.
const PUT_IDLE_TIMEOUT_MS = 120000;
const ONLINE_RECHECK_MS = 5000;

const putError = (message, props) =>
  Object.assign(new Error(message), { status: 0, ...props });

const abortedError = () =>
  putError("Upload stopped.", { code: "aborted", name: "AbortError" });

const responseHeader = (xhr, name) => {
  try {
    return xhr.getResponseHeader(name);
  } catch (error) {
    return null;
  }
};

export function createWebAdapter({
  getWindow = () => (typeof window !== "undefined" ? window : undefined),
  XMLHttpRequestImpl,
  idleTimeoutMs = PUT_IDLE_TIMEOUT_MS,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (id) => clearTimeout(id),
} = {}) {
  let leaveHandler = null;

  const isOnline = () => {
    const nav = getWindow()?.navigator;
    return !(nav && nav.onLine === false);
  };

  return {
    platform: "web",
    // Parts in flight: start at 3, 2 on slow links, at most 4 (core.js).
    defaults: { initialConcurrency: 3, minConcurrency: 2, maxConcurrency: 4 },

    describeFile(file) {
      return {
        name: String(file?.name || "video"),
        size: Number(file?.size) || 0,
        lastModified: Number.isFinite(file?.lastModified)
          ? file.lastModified
          : null,
        type: typeof file?.type === "string" ? file.type : "",
      };
    },

    // No content type on the slice, so the browser sends the part without a
    // Content-Type header.
    readPart(file, start, end) {
      return file.slice(start, end);
    },

    putPart(url, body, onProgress, signal) {
      return new Promise((resolve, reject) => {
        if (signal?.aborted) {
          reject(abortedError());
          return;
        }
        const Request =
          XMLHttpRequestImpl ||
          getWindow()?.XMLHttpRequest ||
          globalThis.XMLHttpRequest;
        const xhr = new Request();
        let settled = false;
        let idleTimer = null;

        // Settle before xhr.abort(): it fires onabort synchronously.
        const stop = (error) => {
          finish(reject, error);
          try {
            xhr.abort();
          } catch (abortError) {
            // already finished
          }
        };
        const onAbort = () => stop(abortedError());

        function finish(settle, value) {
          if (settled) return;
          settled = true;
          clearTimer(idleTimer);
          signal?.removeEventListener?.("abort", onAbort);
          settle(value);
        }

        const armIdleTimer = () => {
          clearTimer(idleTimer);
          idleTimer = setTimer(
            () => stop(putError("The upload stalled.", { code: "timeout" })),
            idleTimeoutMs
          );
        };

        xhr.open("PUT", url, true);
        if (xhr.upload) {
          xhr.upload.onprogress = (event) => {
            armIdleTimer();
            if (typeof event?.loaded === "number") onProgress?.(event.loaded);
          };
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            finish(resolve, { etag: responseHeader(xhr, "ETag") });
          } else {
            finish(
              reject,
              putError(`Part upload failed (${xhr.status}).`, {
                status: xhr.status,
                code: "http",
                body: String(xhr.responseText || "").slice(0, 500),
              })
            );
          }
        };
        xhr.onerror = () =>
          finish(reject, putError("Network error.", { code: "network" }));
        xhr.ontimeout = () =>
          finish(reject, putError("The upload stalled.", { code: "timeout" }));
        xhr.onabort = () =>
          finish(
            reject,
            signal?.aborted
              ? abortedError()
              : putError("Network error.", { code: "network" })
          );
        signal?.addEventListener?.("abort", onAbort, { once: true });
        armIdleTimer();
        xhr.send(body);
      });
    },

    isOnline,

    waitForOnline(signal) {
      return new Promise((resolve, reject) => {
        if (signal?.aborted) {
          reject(abortedError());
          return;
        }
        if (isOnline()) {
          resolve();
          return;
        }
        const win = getWindow();
        let recheck = null;
        const cleanup = () => {
          win?.removeEventListener?.("online", onOnline);
          signal?.removeEventListener?.("abort", onAbort);
          clearInterval(recheck);
        };
        const onOnline = () => {
          cleanup();
          resolve();
        };
        const onAbort = () => {
          cleanup();
          reject(abortedError());
        };
        win?.addEventListener?.("online", onOnline);
        signal?.addEventListener?.("abort", onAbort, { once: true });
        // Some browsers miss the event (e.g. after the laptop sleeps).
        recheck = setInterval(() => {
          if (isOnline()) onOnline();
        }, ONLINE_RECHECK_MS);
      });
    },

    // "Leave site?" while parts are being sent.
    setLeaveGuard(active) {
      const win = getWindow();
      if (!win?.addEventListener) return;
      if (active && !leaveHandler) {
        leaveHandler = (event) => {
          event.preventDefault();
          event.returnValue = "";
          return "";
        };
        win.addEventListener("beforeunload", leaveHandler);
      } else if (!active && leaveHandler) {
        win.removeEventListener("beforeunload", leaveHandler);
        leaveHandler = null;
      }
    },

    // May throw (blocked site data); the resume store handles that.
    getStorage() {
      return getWindow()?.localStorage || null;
    },

    clientInfo() {
      const nav = getWindow()?.navigator;
      return {
        platform: "web",
        app_version: "web",
        ua: String(nav?.userAgent || "").slice(0, 256),
      };
    },
  };
}

export const platformAdapter = createWebAdapter();
